import { additionalDataDocumentsForm, UseDocumentFormType } from '../interface'
import { AppContext } from 'context/appContext'
import { AxiosResponse } from 'axios'
import { buildData, getDocumentsFormError } from '../helpers'
import { CategoryType, createEvent } from 'utils/analytics/tracks/events'
import { EFileClassification, WIZARD_STAGES_ID } from 'utils/constants'
import { entitiesUploadFilePost, postSave, postSaveFile } from 'lib/entities'
import { getToken } from 'utils/auth'
import { IActivationsPlansFiles } from 'context/interface'
import { IFileItem, IFormSubmitErrors, IMimeTypes } from '../../../interface'
import { scrollTopV2 } from 'utils/misc'
import { showOnboardingErrorToastAlert } from 'apps/Onboarding/helpers'
import { useContext, useEffect, useState } from 'react'
import { useLoading } from 'apps/Onboarding/views/BlueBook/hooks/useLoading'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export const useDocumentsForm = (files: Array<IActivationsPlansFiles>): UseDocumentFormType => {
  const { appStore, updateWizardData, updateWizardSavedData, wizardData, wizardSavedData } = useContext(AppContext)
  const { t } = useTranslation()
  const { id: ENTITY_ID } = useParams<{ id: string }>()
  const { isLoading, setIsLoading } = useLoading(false)

  const [additionalData, setAdditionalData] = useState<additionalDataDocumentsForm>({
    fiscal: false,
    legal: false
  })
  const [hasInternationalDoc, setHasInternationalDoc] = useState<boolean>(false)
  const [failUpload, setfailUpload] = useState<Record<string, boolean>>({})
  const [isLoadingFile, setIsLoadingFile] = useState<Record<string, boolean>>({})
  const [fileType, setFileType] = useState<any>([])
  const [hasKycProblem, setHasKycProblem] = useState<boolean>(false)

  useEffect(() => {
    if (appStore.fileTypesGet.data.length) {
      setFileType([...appStore.fileTypesGet.data])
    }
  }, [appStore.fileTypesGet.data])

  const updateStage = (resp: AxiosResponse<any, any>): void => {
    if (resp.status == 202) {
      updateWizardData({
        ...wizardData,
        fiscalInfo: {
          ...wizardData.fiscalInfo,
          legalName: resp.data.fiscalInfo.legalName
        },
        stageOnPlate: { id: 2, name: WIZARD_STAGES_ID.additional }
      })
      createEvent('Onboarding Stages', CategoryType.ONBOARDING, '8. Terms and Conditions - Continue')
    }
  }

  const handleDocumentsSubmit = async (
    values: Record<string, IFileItem | string>
  ): Promise<void | IFormSubmitErrors> => {
    setIsLoading(true)
    const SUBMIT_ERRORS: IFormSubmitErrors = {}
    updateWizardSavedData({
      [WIZARD_STAGES_ID.documents]: {
        data: values,
        hasData: true,
        values: wizardSavedData[WIZARD_STAGES_ID.information].values
      }
    })
    try {
      const resp = await postSaveFile({
        entityId: ENTITY_ID || wizardData.entityId
      })
      updateStage(resp)
    } catch (error: any) {
      console.error(error)
      getDocumentsFormError(error, SUBMIT_ERRORS, setHasKycProblem, setAdditionalData, t)
    } finally {
      scrollTopV2()
      setIsLoading(false)
    }

    if (Object.keys(SUBMIT_ERRORS).length > 0) return SUBMIT_ERRORS
  }

  const handleManualInfoSubmit = async (values: Record<string, IFileItem | string>): Promise<void> => {
    const data = buildData(values)
    try {
      setIsLoading(true)
      const resp = await postSave({
        data: data,
        entityId: ENTITY_ID || wizardData.entityId
      })
      updateStage(resp)
    } catch (e) {
      console.error(e)
      showOnboardingErrorToastAlert(t)
    } finally {
      scrollTopV2()
      setIsLoading(false)
    }
  }

  const handleSubmit = (values: Record<string, IFileItem | string>): Promise<void | IFormSubmitErrors> => {
    if (!hasKycProblem) {
      return handleDocumentsSubmit(values)
    }
    return handleManualInfoSubmit(values)
  }

  const handleDocumentSubmit = async (file: any): Promise<void> => {
    const selectedFile = files[file.index || 0]
    const FILE_TYPE = fileType.find(({ mimeType }: IMimeTypes) => mimeType === file.file?.type)
    const VALUES = {
      data: file.file,
      fileClassification: selectedFile.fileClassificationId,
      fileTypeId: FILE_TYPE?.id,
      id: wizardData.entityId || ENTITY_ID,
      international: String(
        hasInternationalDoc && selectedFile.fileClassificationId === EFileClassification.officialIDFront
      ),
      token: getToken()
    }

    try {
      setIsLoadingFile({ ...isLoadingFile, [VALUES.fileClassification]: true })
      await entitiesUploadFilePost({ ...VALUES })
      setfailUpload({ ...failUpload, [VALUES.fileClassification]: false })
    } catch (e) {
      console.error(e)
      showOnboardingErrorToastAlert(t)
      file.rejected = true
      file.file.hasError = true
      setfailUpload({ ...failUpload, [VALUES.fileClassification]: true })
    } finally {
      setIsLoadingFile({ ...isLoadingFile, [VALUES.fileClassification]: false })
    }
  }

  return {
    additionalData,
    failUpload,
    handleDocumentSubmit,
    handleSubmit,
    hasInternationalDoc,
    hasKycProblem,
    isLoading,
    isLoadingFile,
    setHasInternationalDoc
  }
}
